import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Inject,
    Input,
    OnChanges,
    Optional,
    Renderer2,
    Self,
    SimpleChanges,
    HostBinding,
} from "@angular/core";
import { NgControl } from "@angular/forms";
import * as momentImport from "moment-timezone";
import { Duration, Moment } from "moment-timezone";

import { DATE_FORMAT } from "../../../tokens/index";
import {
    dateRangeValidator,
    minDateValidator,
    maxDateValidator,
} from "../../../validators/index";
import { MomentRange, DateSelectionEvent, SelectMode } from "@spscommerce/ds-shared";
import { SpsAbstractDatepicker } from "../sps-abstract-datepicker";

const moment = momentImport as any; // required for rollup to work without a TypeScript error

@Component({
    selector: "sps-date-range-picker",
    templateUrl: "./sps-date-range-picker.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpsDateRangePickerComponent extends SpsAbstractDatepicker<MomentRange>
implements AfterViewInit, OnChanges {
    static readonly displayName: string = "sps-date-range-picker";
    static readonly props = {
        ...SpsAbstractDatepicker.props,
        availablePresets: "Array<Duration>",
    };

    private static _idNum = 0;
    @HostBinding("class.sps-datepicker") readonly _spsDatepickerClass = true;
    @HostBinding("class.sps-date-range-picker") readonly _spsDateRangePickerClass = true;

    @Input() availablePresets: Array<Duration> = [
        { days: 1 },
        { days: 7 },
        { days: 30 },
        { days: 60 },
        { days: 90 },
        { years: 1 }
    ].map(moment.duration);

    _idNum: number;

    @HostBinding("class.sps-date-range-picker--preset-selected")
    _preset: Duration;

    constructor(
        _changeDetector: ChangeDetectorRef,
        @Inject(DATE_FORMAT) _dateFormat: string,
        _el: ElementRef,
        _renderer: Renderer2,
        @Self()
        @Optional()
        _ngControl: NgControl
    ) {
        super(_changeDetector, _dateFormat, _el, _renderer, _ngControl);
        this.placeholder = `${this.formatString} - ${this.formatString}`;
        this._idNum = SpsDateRangePickerComponent._idNum++;
    }

    clear() {
        this.value = undefined;
        this._preset = undefined;
        this.updateText();
        setTimeout(() => this.inputElement.nativeElement.focus(), 0);
    }

    close() {
        super.close();
        if (this.value && !this.value.end) {
            this.value.end = this.value.start.clone().endOf("day");
            this.updateText();
            this.calendar.selectMode = SelectMode.START;
            this.calendar.clearMouseoverRange();
            this._propagateChange(this.value);
        }
    }

    handleDateSelected(event: DateSelectionEvent) {
        this._preset = undefined;
        if (event.mode === SelectMode.START) {
            this.value = { start: (event.value as Moment), end: null };
        } else {
            this.value = (event.value as MomentRange);
            delete this.keyboardFocusDay;
            this.inputElement.nativeElement.focus();
            this.close();
        }
        this.updateText();
    }

    getPresetText(preset?: Duration): string {
        if (!preset) {
            return;
        }
        if (preset.asDays() === 1) {
            return "Today"; // today is a special snowflake
        }
        return `Last ${preset.humanize().replace('a ', '')}`;
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);

        if (changes.availablePresets && this.availablePresets) {
            this.availablePresets = this.availablePresets.map(moment.duration);
        }

        if ((changes.availablePresets || changes.minDate || changes.maxDate) && this.availablePresets && this.calendar) {
            const today = moment();
            const todayIsAllowed = !this.calendar.dateIsDisabled(today);
            this.availablePresets = this.availablePresets.filter(preset =>
                todayIsAllowed && !this.calendar.dateIsDisabled(today.subtract(preset))
            );
        }

        if (changes.availablePresets) {
            setTimeout(() => {
                if (this._preset) {
                    this.updateFromPreset(this._preset);
                }
            }, 0);
        }
    }

    parse(input: string): MomentRange {
        if (input) {
            const split = input.split("-");
            let startStr, endStr;

            if (split.length > 2) {
                let prev;
                for (let i = 0; i <= Math.ceil(input.length / 2); i++) {
                    const sub = input.substr(i, input.length - 2 * i);
                    if (sub.indexOf("-") === -1) {
                        startStr = input.substring(0, i + prev.indexOf("-") - 1);
                        endStr = input.substring(i + prev.indexOf("-"));
                        break;
                    }
                    prev = sub;
                }
            } else {
                [startStr, endStr] = split;
            }

            const startDateAtMidnight = `${startStr} 00:00:00`;
            const endDateAtMidnight = `${endStr} 00:00:00`;
            return {
                start: moment(`${startDateAtMidnight}${moment(startDateAtMidnight).format("Z")}`),
                end: moment(`${endDateAtMidnight}${moment(endDateAtMidnight).format("Z")}`)
            };
        }
    }

    updateText() {
        this.text = this.value
            ? [
                this.value.start ? this.value.start.format(this._dateFormat) : "",
                this.value.end ? this.value.end.format(this._dateFormat) : ""
            ].join(" - ")
            : "";
    }

    onNewPresetSelected() {
        this.updateFromPreset(this._preset);
        this._propagateChange(this.value);
        this.close();
    }

    updateFromPreset(newPreset: Duration) {
        const matchedPreset = this.matchPreset(newPreset);

        if (matchedPreset) {
            this._preset = matchedPreset;
            const today = moment();
            let startDate;
            let endDate;

            if (newPreset.asDays() < 1) {
                startDate = today.clone().subtract(matchedPreset);
                endDate = today;
            } else {
                startDate = today.clone().subtract(matchedPreset).add(1, "day").startOf("day");
                endDate = today.endOf("day");
            }
            this.value = {
                preset: matchedPreset,
                start: startDate,
                end: endDate
            };
        } else {
            this.value = undefined;
            this._preset = undefined;
        }
        this.updateText();
    }

    matchPreset(newPreset: Duration): Duration {
        if (this.availablePresets && newPreset) {
            const match = this.availablePresets.find(preset =>
                preset.asMilliseconds() === moment.duration(newPreset).asMilliseconds()
            );
            return match;
        }
        return undefined;
    }

    writeValue(newValue: MomentRange) {
        if (newValue && newValue.preset) {
            this.updateFromPreset(newValue.preset);
        } else {
            super.writeValue(newValue);
            this._preset = undefined;
        }
    }

    validate() {
        if (this.value && this._ngControl && this._ngControl.control) {
            this._ngControl.control.setErrors(
                dateRangeValidator(this._ngControl.control)
                || minDateValidator(this._minDateMoment)(this._ngControl.control)
                || maxDateValidator(this._maxDateMoment)(this._ngControl.control)
            );
        }
    }

    setView(displayMoment?: Moment) {
        this.validate();

        if (this.value) {
            if (this.value.end && this.value.end.isValid()) {
                displayMoment = this.value.end.clone();
            } else {
                return;
            }
        } else if (this.maxDate) {
            displayMoment = this._maxDateMoment.clone();
        } else {
            displayMoment = moment();
        }
        displayMoment.startOf("month");
        const viewStart = displayMoment.clone().subtract(1, "month");

        this.view = { start: viewStart, end: displayMoment };
        this._changeDetector.markForCheck();
    }

    focusCalendar() {
        super.focusCalendar();
        this.keyboardFocusDay = this.view.start.clone();
    }

    @HostListener("keydown", ["$event"])
    onKeyDown(event: KeyboardEvent) {
        super.onKeyDown(event);
        this.calendar.onKeyDown(event);
    }
}
